import { useState } from 'react';
import { Button } from '@/components/atoms/Button';
import { ErrorScreen } from '@/components/organisms/AppErrorBoundary';
import { ErrorState } from '@/components/molecules/ErrorState';
import styles from './ErrorPreviewPage.module.scss';

/**
 * Página TEMPORAL para revisar a ojo todas las pantallas de error.
 * Ruta: /preview/errors. Se puede borrar cuando ya no haga falta.
 */
function Bomb(): never {
  throw new Error('Preview: error de render de prueba');
}

export function ErrorPreviewPage() {
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [boom, setBoom] = useState(false);

  // Provoca un error de render real → lo captura el RouteErrorBoundary y
  // muestra la pantalla de marca de verdad (no esta simulación).
  if (boom) return <Bomb />;

  // Muestra la pantalla de error completa "en frío" (sin lanzar error),
  // para revisar su aspecto. El botón "Recargar" aquí solo vuelve atrás.
  if (showFullScreen) {
    return <ErrorScreen onReload={() => setShowFullScreen(false)} />;
  }

  return (
    <div className={styles.preview}>
      <header className={styles.preview__header}>
        <h1 className={styles.preview__title}>Preview de pantallas de error</h1>
        <p className={styles.preview__note}>
          Página temporal para revisar el diseño. Pídeme que la borre cuando termines.
        </p>
      </header>

      <section className={styles.preview__section}>
        <h2 className={styles.preview__h2}>1. Pantalla de error a página completa</h2>
        <p className={styles.preview__desc}>
          La que sale ante un fallo de render (boundary global y de rutas).
        </p>
        <div className={styles.preview__actions}>
          <Button variant="outline" onClick={() => setShowFullScreen(true)}>
            Ver pantalla (simulada)
          </Button>
          <Button variant="primary" onClick={() => setBoom(true)}>
            Provocar error de render real
          </Button>
        </div>
      </section>

      <section className={styles.preview__section}>
        <h2 className={styles.preview__h2}>2. Estado de error con &quot;Reintentar&quot;</h2>
        <p className={styles.preview__desc}>
          El bloque que aparece dentro de las páginas de alumno cuando falla una
          carga (con textos por defecto y, debajo, personalizados).
        </p>
        <div className={styles.preview__grid}>
          <ErrorState onRetry={() => alert('Reintentar pulsado')} />
          <ErrorState
            title="No hemos podido cargar el ranking"
            description="Vuelve a intentarlo en unos segundos."
            onRetry={() => alert('Reintentar pulsado')}
          />
          <ErrorState title="Sin botón de reintentar" description="Variante sin acción." />
        </div>
      </section>

      <section className={styles.preview__section}>
        <h2 className={styles.preview__h2}>3. Página offline del Service Worker</h2>
        <p className={styles.preview__desc}>
          Es un HTML autónomo. Ábrelo directamente:
        </p>
        <div className={styles.preview__actions}>
          <a className={styles.preview__link} href="/offline.html" target="_blank" rel="noreferrer">
            Abrir /offline.html
          </a>
        </div>
      </section>

      <section className={styles.preview__section}>
        <h2 className={styles.preview__h2}>4. Página 404</h2>
        <p className={styles.preview__desc}>Visita una URL inexistente:</p>
        <div className={styles.preview__actions}>
          <a
            className={styles.preview__link}
            href="/esta-ruta-no-existe"
            target="_blank"
            rel="noreferrer"
          >
            Abrir una URL inexistente
          </a>
        </div>
      </section>

      <section className={styles.preview__section}>
        <h2 className={styles.preview__h2}>5. Banner global &quot;sin conexión&quot;</h2>
        <p className={styles.preview__desc}>
          Se dispara con el estado real del navegador. Para verlo: DevTools →
          pestaña Network (o Application) → marca &quot;Offline&quot;. Aparecerá la
          franja arriba del todo.
        </p>
      </section>
    </div>
  );
}

export default ErrorPreviewPage;
